import * as THREE from 'three';
import EffectShell from './EffectShell';
import gsap, { Power4 } from 'gsap';
require('./Math.js');

export default class Effect extends EffectShell {
	constructor(container = document.body, itemsWrapper = null, store = null, options = {}) {
		super(container, itemsWrapper, store);
		if(!this.container || !this.itemsWrapper || !this.store) return

		options.strength = options.strength || 0.25;
		this.options = options;

		this.init();
	}

	init() {
		this.position = new THREE.Vector3(0, 0, 0);
		this.scale = new THREE.Vector3(1, 1, 1);
		// This for transition
		//this.geometry = new THREE.PlaneBufferGeometry(6, 2.2, 32, 32);
		this.geometry = new THREE.PlaneBufferGeometry(1, 1, 32, 32);
		this.uniforms = {
			uTexture: {
				// Texture data
				value: null
			},

			uOffset: {
				// Distortion Strength
				value: new THREE.Vector2(0.0, 0.0)
			},

			uAlpha: {
				// Opacity
				value: 0
			}
		}
	
		this.material = new THREE.ShaderMaterial({
			uniforms: this.uniforms,
			/* Basic Shader 
			vertexShader: `
				uniform vec2 uOffset;
	       		varying vec2 vUv;
	 			
	      		void main() {
	      			vUv = uv;
	      			vec3 newPosition = position;
	      			gl_Position = projectionMatrix * modelViewMatrix * vec4( newPosition, 1.0 );
	      		}
			`, */
			/* Motion Shader (no stretch)
			vertexShader: `
				uniform vec2 uOffset;
	       		varying vec2 vUv;
	 			
	       		#define M_PI 3.1415926535897932384626433832795

	       		vec3 deformationCurve(vec3 position, vec2 uv, vec2 offset) {
	       			position.x = position.x + (sin(uv.y * M_PI) * offset.x);
	       			position.y = position.y + (sin(uv.x * M_PI) * offset.y);
	       			return position;
	       		}

	      		void main() {
	      			vUv = uv;	      			
	      			vec3 newPosition = deformationCurve(position, uv, uOffset);
	      			gl_Position = projectionMatrix * modelViewMatrix * vec4( newPosition, 1.0 );
	      		}
			`,*/

			/* Motion Shader (with Stretch) */
			vertexShader: `
				uniform vec2 uOffset;
	       		varying vec2 vUv;	 				       	

	       		#define WAVE_INTENSITY .3

	       		vec3 deformationCurve(vec3 position, vec2 uv, vec2 offset) {
	       			float M_PI = 3.1415926535897932384626433832795;
	       			position.x = position.x + (sin(uv.y * M_PI) * offset.x) * WAVE_INTENSITY;
	       			position.y = position.y + (sin(uv.x * M_PI) * offset.y) * WAVE_INTENSITY;
	       			return position;
	       		}

	      		void main() {
	      			vUv = uv + (uOffset * 2.);	      			
	      			vec3 newPosition = deformationCurve(position, uv, uOffset);
	      			gl_Position = projectionMatrix * modelViewMatrix * vec4( newPosition, 1.0 );
	      		}
			`,

			/* Regular fragment shader with no zoom 
			fragmentShader: `
				uniform sampler2D uTexture;
				uniform float uAlpha;
				varying vec2 vUv;				

				void main() {
					vec3 color = texture2D(uTexture, vUv).rgb;
					gl_FragColor = vec4(color, uAlpha);
				}
			`,*/

			/* With Zoom Scaling */
			fragmentShader: `
				uniform sampler2D uTexture;
				uniform float uAlpha;
				varying vec2 vUv;

				#define ZOOM 0.9

				// Zoom on texture
				vec2 scaleUV(vec2 uv, float scale) {
					float center = 0.5;
					return ((uv -center) * scale) + center;
				}

				void main() {
					vec3 color = texture2D(uTexture, scaleUV(vUv, ZOOM)).rgb;
					gl_FragColor = vec4(color, uAlpha);
				}
			`,
			transparent: true
		});

		this.plane = new THREE.Mesh(this.geometry, this.material);
		this.scene.add(this.plane);
	}

	onMouseEnter() {
		if(!this.currentItem || !this.isMouseOver) {
			this.isMouseOver = true;			

			// Show Plane
			gsap.to(this.uniforms.uAlpha, 0.5, {
				value: 1,
				ease: Power4.easeOut
			});
		}
	}

	onMouseLeave() {
		gsap.to(this.uniforms.uAlpha, 0.5, {
			value: 0,
			ease: Power4.easeOut
		});
	}

	onMouseOver(index) {
		if(!this.isLoaded) return
		this.onMouseEnter();
		if(this.currentItem && this.currentItem.index === index) return
		this.onTargetChange(index);
	}

	/*onMouseClick() {		
		gsap.to(this.uniforms.uAlpha, {
			duration: 0.5,
			value: 0,			
			overwrite: true,
			onComplete: ()=> { console.log("HI")},
			ease: "power4.out"
		});
	}*/

	onMouseMove() {		

		// Project mouse to world coordinates
		let x = this.mouse.x.map(
			-1,
			1,
			-this.viewSize.width / 2,
			this.viewSize.width / 2
		);
		//console.log(this.mouse.y);
		let y = this.mouse.y.map(
			-1,
			1,
			-this.viewSize.height / 2,
			this.viewSize.height / 2
		);		

		// Update plane position
		this.position = new THREE.Vector3(x, y, 0);

		gsap.to(this.plane.position, 1, {
			x: x,
			y: y,
			ease: Power4.easeOut,
			onUpdate: this.onPositionUpdate.bind(this)
		});

	}

	onTargetChange(index) {
		// Item target changed
		this.currentItem = this.items[index];
		if(!this.currentItem.texture) return		

		// Update texture
		this.uniforms.uTexture.value = this.currentItem.texture;		

		// Compute Image Ratio
		let imageRatio = this.currentItem.texture.image.width / this.currentItem.texture.image.height;

		// Scale plane to fit image dimensions
		this.scale = new THREE.Vector3(imageRatio, 1, 1);
		this.plane.scale.copy(this.scale);
	}

	onPositionUpdate() {
		// Compute Offset
		let offset = this.plane.position
			.clone()
			.sub(this.position) // Velocity
			.multiplyScalar(-this.options.strength)
		this.uniforms.uOffset.value = offset;
	}
}